import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'statarena-language';
const SUPPORTED_LANGUAGES = ['hr', 'en'];

const translations = {
  hr: {
    common: {
      brandBall: 'Nogometna lopta',
      loading: 'Učitavanje...',
      language: 'Jezik',
    },
    nav: {
      home: 'Početna',
      management: 'Upravljanje timom',
      profile: 'Profil igrača',
      division: 'Podjela Timova',
      teams: 'Popis timova',
      privacy: 'Privatnost',
      settings: 'Postavke tima',
      logout: 'Odjava',
      login: 'Prijava',
      register: 'Registracija',
      registerCta: 'Registriraj se',
      features: 'Mogućnosti',
      howItWorks: 'Kako radi',
      tools: 'Alati',
      games: 'Igre',
    },
    auth: {
      loginTitle: 'Prijava',
      loginAria: 'Prijava korisnika',
      loginError: 'Neispravni podaci za prijavu.',
      emailLabel: 'Email adresa',
      passwordLabel: 'Lozinka',
      nameLabel: 'Ime i prezime',
      namePlaceholder: 'Ime i prezime',
      loginSubmit: 'Prijavi se',
      forgotPassword: 'Zaboravljena lozinka?',
      noAccount: 'Nemate račun?',
      registerHere: 'Registrirajte se ovdje',
      registerTitle: 'Registracija',
      registerAria: 'Registracija korisnika',
      registerSubmit: 'Registriraj se',
      alreadyAccount: 'Već imate račun?',
      loginHere: 'Prijavite se ovdje',
      verificationSent: 'Poslali smo vam verifikacijski email. Provjerite svoj inbox!',
      welcomeTeamsStart: 'Dobrodošao! Pronašli smo',
      waitingTeam: 'tim koji te već čeka.',
      waitingTeams: 'tima koji te već čekaju.',
      goToLogin: 'Idi na prijavu',
      forgotTitle: 'Zaboravljena lozinka',
      forgotEmpty: 'Unesi svoj email.',
      forgotInvalid: 'Email format nije ispravan.',
      forgotTooMany: 'Previše pokušaja. Pokušaj opet za par minuta.',
      forgotSentStart: 'Ako postoji račun povezan s adresom',
      forgotSentEnd: 'poslali smo ti email s linkom za postavljanje nove lozinke.',
      forgotSpam: 'Provjeri i Spam / Junk folder ako ne vidiš mail u Inboxu za par minuta.',
      backToLogin: 'Natrag na prijavu',
      forgotIntro: 'Upiši email kojim si se registrirao i poslat ćemo ti link za postavljanje nove lozinke.',
      resetAria: 'Reset lozinke',
      sending: 'Šaljem...',
      sendReset: 'Pošalji link za reset',
      remembered: 'Sjetio si se?',
    },
    landing: {
      seoTitle: 'StatArena - Rekreativni nogomet, raspored i statistika',
      seoDescription:
        'Besplatna aplikacija za rekreativni nogomet. Organiziraj raspored termina, podijeli timove automatski, prati statistiku igrača i MVP rang listu.',
      toolsMenu: [
        ['Generator timova', 'Nasumična podjela igrača u timove - bez registracije.'],
        ['Raspored turnira', 'Generator rasporeda svi protiv svih i kup sustav.'],
        ['Online semafor', 'Digitalni semafor s timerom, rezultatom i kartonima.'],
      ],
      gamesMenu: [
        ['Penalty shootout', 'Simulator jedanaesteraca - pucaj i brani protiv kompjutera.'],
        ['Koji si tip igrača?', 'Quiz - otkrij kojeg poznatog nogometaša najviše podsjećaš.'],
        ['Nogomandala', 'Pogodi tajnog nogometaša u 8 pokušaja - novi puzzle svaki dan.'],
      ],
      eyebrow: 'Besplatno · Bez instalacije',
      titleStart: 'Raspored, timovi i statistika na',
      titleAccent: 'jednom mjestu',
      subtitle:
        'StatArena je besplatna web aplikacija za rekreativni nogomet, futsal i firmaške ekipe. Manje dogovaranja u grupnom chatu — više igre.',
      registerFree: 'Registriraj se besplatno',
      viewTeams: 'Pogledaj timove →',
      heroNote: 'Igrači se ne moraju registrirati — samo organizator otvara tim.',
      featuresTitle: 'Organiziraj nogometni termin bez Excela i grupnog chata',
      featuresSub:
        'Vodi termine, podijeli ekipe, upiši rezultate i prati statistiku igrača kroz sezonu. StatArena je napravljena za mali nogomet, futsal i rekreativne ekipe koje žele manje dogovaranja, a više igre.',
      features: [
        ['Kalendar', '📅', 'Raspored termina', 'Jedan link za cijelu ekipu. Organizator objavi termine, igrači vide kada se igra, a sve ostaje na jednom mjestu bez traženja poruka po chatu.'],
        ['Vaga', '⚖️', 'Automatska podjela timova', 'Pametni algoritam dijeli igrače u balansirane timove prema njihovoj statistici i formi. Pravedno, brzo i bez svađe na terenu.'],
        ['Statistika', '📊', 'Statistika igrača', 'Golovi, asistencije, pobjede, porazi, prosjek po utakmici. Svaki igrač ima svoj profil i grafički prikaz napretka kroz sezonu.'],
        ['Trofej', '🏆', 'Rang lista (MVP)', 'Tko je MVP ove sezone? Rang lista se automatski ažurira nakon svake utakmice. Motivacija za trening i razlog za proslavu na kraju sezone.'],
        ['Ekipa', '👥', 'Više timova na jednom računu', 'Vodite više ekipa? Jedan račun, više timova. Svaki tim ima svoj raspored, svoju rang listu i svoju arhivu - potpuno odvojeno.'],
        ['Arhiva', '🗂️', 'Arhiva sezona', 'Sve prošle sezone ostaju zapisane. Pogledaj tko je bio najbolji strijelac i uspoređuj sezone kroz godine.'],
      ],
      howTitle: 'Kako radi StatArena',
      howSub: 'Od prve prijave do prve utakmice - manje od 5 minuta.',
      steps: [
        ['Registriraj se', 'Brza registracija e-mailom. Aplikacija je besplatna za tvoju ekipu.'],
        ['Kreiraj tim', 'Otvori svoj tim, dodaj igrače i pošalji im link za prijavu.'],
        ['Zakaži termin', 'Datum, sat, lokacija - i ekipa se prijavljuje, a algoritam slaže timove.'],
        ['Prati statistiku', 'Unesi rezultat i aplikacija automatski ažurira statistiku i rang listu.'],
      ],
      audienceTitle: 'Za koga je StatArena?',
      audience: [
        ['Zgrada firme', '🏢', 'Firma koja igra nogomet srijedom', 'Umjesto kaosa u grupnom chatu, jedan link za prijavu i jasni timovi.'],
        ['Ekipa prijatelja', '👥', 'Ekipa prijatelja', 'Cijela sezona zapisana. Tko je MVP? Tko ima najviše golova? Statistika ne laže.'],
        ['Nogomet', '⚽', 'Amaterski klubovi i rekreativne lige', 'Vodi više timova, prati rezultate i rang listu - sve iz jednog sučelja.'],
      ],
      faqTitle: 'Često postavljena pitanja o aplikaciji StatArena',
      faqSub:
        'Sve što trebaš znati prije nego što kreneš organizirati rekreativni nogomet s tvojom ekipom na jedan moderan i jednostavan način.',
      faqs: [
        ['Što je StatArena?', 'StatArena je besplatna web aplikacija dizajnirana specifično za rekreativni nogomet - mali nogomet, futsal, firmaške lige i prijateljske termine. Pokriva cijeli ciklus organizacije: od kreiranja rasporeda termina, preko automatske podjele timova prema statistici igrača, pa sve do detaljne MVP rang liste i arhive prošlih sezona. Aplikacija radi u pregledniku, bez instalacije.'],
        ['Je li aplikacija stvarno besplatna?', 'Da, StatArena je u potpunosti besplatna za rekreativni nogomet. Nema skrivenih troškova, nema pretplate, nema premium razine. Organizator otvara tim, dodaje igrače, zakazuje termine i prati statistiku bez ikakvih ograničenja. Naš cilj je da što više ekipa lakše organizira svoje nogometne termine i da zadrži svu svoju povijest na jednom mjestu.'],
        ['Kako funkcionira automatska podjela timova?', 'Algoritam za podjelu timova uzima u obzir statistiku svakog igrača iz prethodnih utakmica - golove, asistencije, pobjede i prosječnu ocjenu - te formira dva ili više balansiranih timova. Cilj je da svaka strana ima približno jednaku ukupnu jačinu, a samim tim i pravedniju utakmicu bez dugih rasprava prije prvog sudačkog zvižduka.'],
        ['Kakvu statistiku pratim za svakog igrača?', 'Za svakog igrača StatArena prati broj odigranih utakmica, postignute golove, asistencije, pobjede, poraze, omjer pobjeda, prosjek golova po utakmici i ukupnu MVP ocjenu. Svi podaci prikazuju se u pregledu profila igrača, a rang lista MVP-a automatski se ažurira nakon što organizator unese rezultat odigranog termina.'],
        ['Mogu li voditi više nogometnih ekipa odjednom?', 'Apsolutno. Jedan StatArena račun može imati više timova - idealno za organizatore firmaških liga, trenere koji vode amaterske klubove ili rekreativce koji igraju u više grupa kroz tjedan. Svaki tim ima svoj vlastiti raspored, svoju rang listu i svoju arhivu sezona, potpuno odvojeno od ostalih timova na računu.'],
        ['Radi li aplikacija na mobitelu?', 'StatArena je u potpunosti prilagođena mobilnim uređajima i radi u svakom modernom pregledniku na Android i iOS telefonima. Možeš je dodati na početni zaslon kao prečac i koristiti kao standardnu aplikaciju - igrači se prijavljuju na termin jednim klikom, a organizator vidi popis u realnom vremenu dok je na putu prema terenu.'],
      ],
      ctaTitle: 'Spreman da digneš svoj termin na novi nivo?',
      ctaText: 'Registracija traje manje od minute. Dodaj prvi tim i pozovi ekipu - ostalo rješava StatArena.',
      haveAccount: 'Već imam račun',
      footerTag: 'Aplikacija za rekreativni nogomet — raspored, timovi i statistika.',
      app: 'Aplikacija',
      faqShort: 'Česta pitanja',
      toolsAndGames: 'Alati i igre',
      account: 'Račun',
      legal: 'Pravno',
      privacyRules: 'Pravila privatnosti',
      terms: 'Uvjeti korištenja',
      deleteAccount: 'Brisanje računa',
      footballWiki: 'Nogomet (Wikipedia)',
      footballFederation: 'Hrvatski nogometni savez',
    },
    notFound: {
      seoTitle: 'Stranica nije pronađena | StatArena',
      seoDescription:
        'Ova StatArena stranica ne postoji ili je premještena. Vrati se na početnu, direktorij timova ili besplatne nogometne alate.',
      title: 'Stranica nije pronađena',
      body:
        'Link koji si otvorio ne postoji, promijenjen je ili više nije dostupan. Možda je tim obrisan, ruta krivo upisana ili je stari link ostao negdje u chatu.',
      home: 'Na početnu',
      viewTeams: 'Pogledaj timove',
      teamGenerator: 'Generator timova',
      usefulLinks: 'Korisni linkovi',
      tournamentSchedule: 'Raspored turnira',
      scoreboard: 'Online semafor',
    },
    legal: {
      badge: 'Pravno',
      privacyTitle: 'Pravila privatnosti',
      privacyDescription: 'Kako StatArena prikuplja, koristi i čuva podatke korisnika i timova.',
      termsTitle: 'Uvjeti korištenja',
      termsDescription: 'Osnovna pravila za korištenje StatArene i podataka u timovima.',
      deletionTitle: 'Brisanje računa i podataka',
      deletionDescription: 'Kako korisnik može zatražiti brisanje računa, profila i povezanih podataka.',
      updated: 'Zadnje ažuriranje: 25. travnja 2026.',
      privacySections: [
        ['Podaci koje spremamo', 'Kada otvoriš račun, spremamo osnovne podatke potrebne za rad aplikacije: email adresu, korisnički identifikator, ime ili prikazno ime i vrijeme kreiranja ili ažuriranja profila. Kada kreiraš ili koristiš tim, spremamo podatke o timu, igračima, članovima, rasporedu, utakmicama i statistikama.'],
        ['Zašto ih koristimo', 'Podatke koristimo za prijavu, verifikaciju računa, prikaz tvojih timova, povezivanje pozvanih igrača s njihovim računom, vođenje statistike i osnovnu administraciju aplikacije. Ne prodajemo korisničke podatke.'],
        ['Tko može vidjeti podatke', 'Javni timovi i njihove statistike mogu biti vidljivi posjetiteljima koji imaju link ili pregledavaju direktorij. Podaci privatnih i zaključanih timova trebaju se koristiti samo u skladu s postavkama pristupa tima. Vlasnik tima vidi i upravlja podacima članova svojeg tima.'],
        ['Servisi koje koristimo', 'StatArena koristi Firebase za autentikaciju, bazu podataka i hosting aplikacijskih podataka. Ako se u budućnosti uključi analitika ili drugi servis, ova pravila će se ažurirati prije ili u trenutku uključivanja.'],
        ['Brisanje i kontakt', 'Možeš zatražiti izvoz, ispravak ili brisanje svojih podataka. Za zahtjev pošalji email s email adrese povezane s računom.'],
      ],
      termsSections: [
        ['Korištenje aplikacije', 'StatArena je alat za organizaciju rekreativnog nogometa, rasporeda, timova i statistike. Koristi aplikaciju zakonito, pošteno i bez pokušaja neovlaštenog pristupa tuđim računima, timovima ili podacima.'],
        ['Odgovornost vlasnika tima', 'Vlasnik tima odgovoran je za podatke koje unosi, uključujući imena, email adrese pozvanih članova, rezultate i statistike. Unosi samo podatke koje imaš pravo koristiti i ukloni podatke ako član tima to opravdano zatraži.'],
        ['Dostupnost i promjene', 'Aplikacija se razvija i može se mijenjati. Trudimo se sačuvati podatke i stabilnost servisa, ali ne garantiramo neprekidan rad bez grešaka.'],
        ['Zabrane', 'Nije dopušteno zloupotrebljavati aplikaciju, automatizirano opterećivati servis, objavljivati uvredljiv ili nezakonit sadržaj, niti pokušavati zaobići sigurnosna pravila.'],
        ['Kontakt', 'Za pitanja o uvjetima korištenja javi se na navedenu kontakt adresu.'],
      ],
      deletionSections: [
        ['Kako zatražiti brisanje', 'Pošalji zahtjev s email adrese koja je povezana s tvojim StatArena računom. U poruci navedi da želiš brisanje računa i, ako je bitno, nazive timova na koje se zahtjev odnosi.'],
        ['Što se briše', 'Brišemo korisnički profil i podatke koji su direktno vezani uz račun. Ako si vlasnik tima, možemo obrisati i tim koji si kreirao, uključujući članove, utakmice, raspored i statistike tog tima.'],
        ['Podaci u tuđim timovima', 'Ako si član tima koji vodi druga osoba, dio statistike može ostati u timskoj evidenciji radi integriteta rezultata. Na zahtjev možemo ukloniti ili anonimizirati osobne oznake poput email adrese i prikaznog imena.'],
        ['Rok obrade', 'Zahtjeve obrađujemo u razumnom roku nakon provjere da zahtjev dolazi s email adrese povezane s računom.'],
      ],
    },
  },
  en: {
    common: {
      brandBall: 'Football',
      loading: 'Loading...',
      language: 'Language',
    },
    nav: {
      home: 'Home',
      management: 'Manage Team',
      profile: 'Player Profile',
      division: 'Team Split',
      teams: 'Team List',
      privacy: 'Privacy',
      settings: 'Team Settings',
      logout: 'Log out',
      login: 'Log in',
      register: 'Sign up',
      registerCta: 'Sign up',
      features: 'Features',
      howItWorks: 'How it works',
      tools: 'Tools',
      games: 'Games',
    },
    auth: {
      loginTitle: 'Log in',
      loginAria: 'User login',
      loginError: 'Invalid login details.',
      emailLabel: 'Email address',
      passwordLabel: 'Password',
      nameLabel: 'Full name',
      namePlaceholder: 'Full name',
      loginSubmit: 'Log in',
      forgotPassword: 'Forgot password?',
      noAccount: "Don't have an account?",
      registerHere: 'Sign up here',
      registerTitle: 'Sign up',
      registerAria: 'User registration',
      registerSubmit: 'Create account',
      alreadyAccount: 'Already have an account?',
      loginHere: 'Log in here',
      verificationSent: 'We sent you a verification email. Check your inbox!',
      welcomeTeamsStart: 'Welcome! We found',
      waitingTeam: 'team already waiting for you.',
      waitingTeams: 'teams already waiting for you.',
      goToLogin: 'Go to login',
      forgotTitle: 'Forgot password',
      forgotEmpty: 'Enter your email.',
      forgotInvalid: 'The email format is not valid.',
      forgotTooMany: 'Too many attempts. Try again in a few minutes.',
      forgotSentStart: 'If there is an account connected to',
      forgotSentEnd: 'we sent you an email with a link to set a new password.',
      forgotSpam: 'Check your Spam / Junk folder if you do not see the email in a few minutes.',
      backToLogin: 'Back to login',
      forgotIntro: 'Enter the email you used to register and we will send you a password reset link.',
      resetAria: 'Password reset',
      sending: 'Sending...',
      sendReset: 'Send reset link',
      remembered: 'Remembered it?',
    },
    landing: {
      seoTitle: 'StatArena - Recreational football schedule and stats',
      seoDescription:
        'A free app for recreational football. Organize sessions, split teams automatically, track player stats and MVP rankings.',
      toolsMenu: [
        ['Team generator', 'Randomly split players into teams - no registration required.'],
        ['Tournament schedule', 'Round-robin and knockout schedule generator.'],
        ['Online scoreboard', 'Digital scoreboard with timer, score and cards.'],
      ],
      gamesMenu: [
        ['Penalty shootout', 'Penalty simulator - shoot and save against the computer.'],
        ['What type of player are you?', 'Quiz - discover which famous footballer you resemble most.'],
        ['Nogomandala', 'Guess the secret footballer in 8 tries - a new puzzle every day.'],
      ],
      eyebrow: 'Free · No installation',
      titleStart: 'Schedule, teams and stats in',
      titleAccent: 'one place',
      subtitle:
        'StatArena is a free web app for recreational football, futsal and company teams. Less planning in group chat — more playing.',
      registerFree: 'Sign up for free',
      viewTeams: 'View teams →',
      heroNote: 'Players do not need to register — only the organizer creates the team.',
      featuresTitle: 'Organize a football session without spreadsheets and group chat chaos',
      featuresSub:
        'Run sessions, split teams, enter results and track player stats through the season. StatArena is built for small-sided football, futsal and recreational teams that want less coordination and more play.',
      features: [
        ['Calendar', '📅', 'Session schedule', 'One link for the whole squad. The organizer publishes sessions, players see when the match is on, and everything stays in one place without searching old chat messages.'],
        ['Scale', '⚖️', 'Automatic team splitting', 'A smart algorithm splits players into balanced teams based on their stats and form. Fair, fast and without arguments on the pitch.'],
        ['Stats', '📊', 'Player statistics', 'Goals, assists, wins, losses and average per match. Every player gets a profile and a visual progress overview through the season.'],
        ['Trophy', '🏆', 'MVP leaderboard', 'Who is this season’s MVP? The leaderboard updates automatically after every match. Motivation for training and a reason to celebrate at season end.'],
        ['Squad', '👥', 'Multiple teams on one account', 'Running more squads? One account, multiple teams. Each team has its own schedule, leaderboard and archive - fully separated.'],
        ['Archive', '🗂️', 'Season archive', 'All previous seasons stay recorded. See who was top scorer and compare seasons over the years.'],
      ],
      howTitle: 'How StatArena works',
      howSub: 'From first login to first match - less than 5 minutes.',
      steps: [
        ['Sign up', 'Quick email registration. The app is free for your squad.'],
        ['Create a team', 'Open your team, add players and send them the signup link.'],
        ['Schedule a session', 'Date, time, location - the squad signs up and the algorithm builds the teams.'],
        ['Track stats', 'Enter the result and the app automatically updates stats and the leaderboard.'],
      ],
      audienceTitle: 'Who is StatArena for?',
      audience: [
        ['Office building', '🏢', 'A company team playing on Wednesdays', 'Instead of group chat chaos, use one signup link and clear teams.'],
        ['Friends squad', '👥', 'A group of friends', 'The whole season is recorded. Who is MVP? Who scored the most goals? The stats do not lie.'],
        ['Football', '⚽', 'Amateur clubs and recreational leagues', 'Run multiple teams, track results and leaderboards - all from one interface.'],
      ],
      faqTitle: 'Frequently asked questions about StatArena',
      faqSub:
        'Everything you need to know before you start organizing recreational football with your squad in a modern, simple way.',
      faqs: [
        ['What is StatArena?', 'StatArena is a free web app designed specifically for recreational football - small-sided football, futsal, company leagues and friendly sessions. It covers the full organization cycle: scheduling sessions, automatically splitting teams by player stats, detailed MVP leaderboards and archives of past seasons. The app runs in the browser, with no installation.'],
        ['Is the app really free?', 'Yes, StatArena is completely free for recreational football. There are no hidden costs, subscriptions or premium tiers. The organizer creates a team, adds players, schedules sessions and tracks stats without limits. Our goal is to help as many squads as possible organize their football sessions more easily and keep their history in one place.'],
        ['How does automatic team splitting work?', 'The team-splitting algorithm considers each player’s stats from previous matches - goals, assists, wins and average rating - then forms two or more balanced teams. The goal is for each side to have similar overall strength, creating a fairer match without long debates before kickoff.'],
        ['What stats can I track for each player?', 'For every player, StatArena tracks matches played, goals, assists, wins, losses, win rate, goals per match and total MVP score. All data appears in the player profile, and the MVP leaderboard updates automatically after the organizer enters the match result.'],
        ['Can I manage multiple football teams at once?', 'Absolutely. One StatArena account can have multiple teams - ideal for company league organizers, coaches managing amateur clubs or recreational players active in several groups during the week. Each team has its own schedule, leaderboard and season archive, fully separate from the others.'],
        ['Does the app work on mobile?', 'StatArena is fully adapted for mobile devices and works in every modern browser on Android and iOS phones. You can add it to your home screen as a shortcut and use it like a standard app - players sign up for a session with one tap, while the organizer sees the list in real time on the way to the pitch.'],
      ],
      ctaTitle: 'Ready to level up your football sessions?',
      ctaText: 'Registration takes less than a minute. Add your first team and invite the squad - StatArena handles the rest.',
      haveAccount: 'I already have an account',
      footerTag: 'App for recreational football — schedule, teams and stats.',
      app: 'App',
      faqShort: 'FAQ',
      toolsAndGames: 'Tools and games',
      account: 'Account',
      legal: 'Legal',
      privacyRules: 'Privacy policy',
      terms: 'Terms of use',
      deleteAccount: 'Account deletion',
      footballWiki: 'Football (Wikipedia)',
      footballFederation: 'Croatian Football Federation',
    },
    notFound: {
      seoTitle: 'Page not found | StatArena',
      seoDescription:
        'This StatArena page does not exist or has moved. Go back to the home page, team directory or free football tools.',
      title: 'Page not found',
      body:
        'The link you opened does not exist, has changed or is no longer available. The team may have been deleted, the route may be mistyped, or an old link may still be sitting in a chat.',
      home: 'Go home',
      viewTeams: 'View teams',
      teamGenerator: 'Team generator',
      usefulLinks: 'Useful links',
      tournamentSchedule: 'Tournament schedule',
      scoreboard: 'Online scoreboard',
    },
    legal: {
      badge: 'Legal',
      privacyTitle: 'Privacy Policy',
      privacyDescription: 'How StatArena collects, uses and protects user and team data.',
      termsTitle: 'Terms of Use',
      termsDescription: 'Basic rules for using StatArena and team data.',
      deletionTitle: 'Account and data deletion',
      deletionDescription: 'How a user can request deletion of an account, profile and related data.',
      updated: 'Last updated: April 25, 2026.',
      privacySections: [
        ['Data we store', 'When you create an account, we store the basic data required for the app to work: email address, user identifier, name or display name, and profile creation or update time. When you create or use a team, we store team, player, member, schedule, match and statistics data.'],
        ['Why we use it', 'We use data for login, account verification, showing your teams, connecting invited players with their accounts, keeping statistics and basic app administration. We do not sell user data.'],
        ['Who can see the data', 'Public teams and their statistics may be visible to visitors who have a link or browse the directory. Data from private and locked teams should only be used according to the team access settings. The team owner can view and manage data for members of that team.'],
        ['Services we use', 'StatArena uses Firebase for authentication, database services and hosting application data. If analytics or another service is added in the future, this policy will be updated before or when that service is enabled.'],
        ['Deletion and contact', 'You can request export, correction or deletion of your data. Send the request from the email address connected to your account.'],
      ],
      termsSections: [
        ['Using the app', 'StatArena is a tool for organizing recreational football, schedules, teams and statistics. Use the app lawfully, fairly and without attempting unauthorized access to other accounts, teams or data.'],
        ['Team owner responsibility', 'The team owner is responsible for the data they enter, including names, email addresses of invited members, results and statistics. Enter only data you have the right to use and remove data if a team member reasonably requests it.'],
        ['Availability and changes', 'The app is under active development and may change. We work to preserve data and service stability, but we do not guarantee uninterrupted or error-free operation.'],
        ['Prohibited use', 'You may not abuse the app, overload the service through automation, publish offensive or unlawful content, or attempt to bypass security rules.'],
        ['Contact', 'For questions about the Terms of Use, contact us at the listed contact address.'],
      ],
      deletionSections: [
        ['How to request deletion', 'Send a request from the email address connected to your StatArena account. In the message, state that you want account deletion and, if relevant, the names of teams the request applies to.'],
        ['What is deleted', 'We delete the user profile and data directly tied to the account. If you are a team owner, we can also delete a team you created, including members, matches, schedules and statistics for that team.'],
        ['Data in other people’s teams', 'If you are a member of a team managed by someone else, some statistics may remain in the team record to preserve result integrity. On request, we can remove or anonymize personal identifiers such as email address and display name.'],
        ['Processing time', 'We process requests within a reasonable time after verifying that the request comes from the email address connected to the account.'],
      ],
    },
  },
};

const staticTextTranslations = {
  en: {
    'Početna': 'Home',
    'Prijava': 'Log in',
    'Registracija': 'Sign up',
    'Registriraj se': 'Sign up',
    'Generator timova': 'Team generator',
    'Raspored turnira': 'Tournament schedule',
    'Online semafor': 'Online scoreboard',
    'Aplikacija za rekreativni nogomet': 'App for recreational football',
    'Besplatno · Bez registracije · Radi na mobitelu': 'Free · No registration · Works on mobile',
    'Imena igrača': 'Player names',
    '(svako u novi red ili odvojeno zarezom)': '(one per line or separated by commas)',
    'Broj timova': 'Number of teams',
    'Igrača po timu:': 'Players per team:',
    'Ukupno timova:': 'Total teams:',
    'Promiješaj i podijeli': 'Shuffle and split',
    'Podjela timova': 'Team split',
    'Promiješaj ponovo': 'Shuffle again',
    'Kopiraj': 'Copy',
    'Kopirano!': 'Copied!',
    'Ubaci primjer': 'Add example',
    'Obriši sve': 'Clear all',
    'Za što sve možeš koristiti generator timova': 'What you can use the team generator for',
    'Kako radi generator timova': 'How the team generator works',
    'Često postavljena pitanja o generatoru timova': 'Team generator FAQ',
    'Unesi imena': 'Enter names',
    'Odaberi broj timova': 'Choose number of teams',
    'Promiješaj': 'Shuffle',
    'Ekipe / timovi': 'Teams',
    '(svaka u novi red ili odvojeno zarezom)': '(one per line or separated by commas)',
    'Format turnira': 'Tournament format',
    'Svi protiv svih': 'Round-robin',
    'Kup / eliminacija': 'Cup / elimination',
    'Tko izgubi - ispada': 'Lose and you are out',
    'Dvokružno (svatko sa svakim dvaput)': 'Double round-robin (everyone plays everyone twice)',
    'Napredne postavke (termin i trajanje)': 'Advanced settings (time and duration)',
    'Početak': 'Start',
    'Trajanje (min)': 'Duration (min)',
    'Pauza (min)': 'Break (min)',
    'Broj terena': 'Number of pitches',
    'Ukupno utakmica:': 'Total matches:',
    'Kola:': 'Rounds:',
    'Rundi:': 'Rounds:',
    'Po kolu:': 'Per round:',
    'Napravi raspored': 'Create schedule',
    'Raspored utakmica': 'Match schedule',
    'Turnirska ljestva (kup)': 'Tournament bracket (cup)',
    'Novi ždrijeb': 'New draw',
    'Isprintaj': 'Print',
    'pobjednik': 'winner',
    'prolaz': 'advance',
    'pauza': 'bye',
    'Za koje sve turnire možeš koristiti generator rasporeda': 'What tournaments you can use the schedule generator for',
    'Kako radi generator rasporeda turnira': 'How the tournament schedule generator works',
    'Često postavljena pitanja o rasporedu turnira': 'Tournament schedule FAQ',
    'Unesi ekipe': 'Enter teams',
    'Odaberi format': 'Choose format',
    'Postavi detalje': 'Set details',
    'Online semafor za nogomet': 'Online football scoreboard',
    'Digitalni semafor za nogomet': 'Digital football scoreboard',
    'Izađi iz full screena': 'Exit full screen',
    'Full screen': 'Full screen',
    'Poluvrijeme': 'Half',
    'Pauza': 'Pause',
    'Start': 'Start',
    'Reset timer': 'Reset timer',
    'Žuti': 'Yellow',
    'Crveni': 'Red',
    'Postavke utakmice': 'Match settings',
    'Trajanje poluvremena': 'Half duration',
    'Smjer brojenja': 'Timer direction',
    'Odbrojavanje': 'Countdown',
    'Broji prema gore': 'Count up',
    'Zvuk na kraj vremena': 'Sound at full time',
    'Boje ekipa': 'Team colors',
    'Sljedeće poluvrijeme': 'Next half',
    'Reset utakmice': 'Reset match',
    'Semafor na TV-u ili tabletu': 'Scoreboard on TV or tablet',
    'Brojač vremena za poluvrijeme': 'Half timer',
    'Rezultat i kartoni na jednom mjestu': 'Score and cards in one place',
    'Često postavljena pitanja o online semaforu': 'Online scoreboard FAQ',
    'Penalty shootout - simulator jedanaesteraca': 'Penalty shootout simulator',
    'TI': 'YOU',
    'pucaš': 'shooting',
    'braniš': 'saving',
    'GOL!': 'GOAL!',
    'OBRANA!': 'SAVE!',
    'Spreman za jedanaesterce?': 'Ready for penalties?',
    'Start meč': 'Start match',
    'Pobjeda!': 'Win!',
    'Poraz': 'Defeat',
    'Izjednačeno': 'Draw',
    'Konačni rezultat:': 'Final score:',
    'Igraj ponovno': 'Play again',
    'Lijevo': 'Left',
    'Sredina': 'Center',
    'Desno': 'Right',
    'Novi meč': 'New match',
    'Kako radi Penalty Shootout?': 'How does Penalty Shootout work?',
    'Pucaj': 'Shoot',
    'Brani': 'Save',
    '5 + 5 udaraca': '5 + 5 shots',
    'Sudden death': 'Sudden death',
    'Često postavljena pitanja o Penalty Shootoutu': 'Penalty Shootout FAQ',
    'Koji si tip igrača?': 'What type of player are you?',
    'Pokreni quiz →': 'Start quiz →',
    'Bez registracije': 'No registration',
    'Bez instalacije': 'No installation',
    'Slika za Instagram & TikTok': 'Image for Instagram & TikTok',
    'TI SI': 'YOU ARE',
    'Tvoje jake strane': 'Your strengths',
    'Pazi na ovo': 'Watch out for this',
    'Savjet': 'Advice',
    'Generiranje...': 'Generating...',
    '📸 Preuzmi sliku za Instagram / TikTok': '📸 Download image for Instagram / TikTok',
    '🔗 Podijeli rezultat': '🔗 Share result',
    '↻ Pokreni ponovo': '↻ Start again',
    'Vidi top 5 najbližih': 'See top 5 closest',
    'Kako radi quiz "Koji si tip igrača?"': 'How does the “What type of player are you?” quiz work?',
    'Odgovori na 10 pitanja': 'Answer 10 questions',
    'Pogledaj rezultat': 'See the result',
    'Preuzmi sliku': 'Download image',
    'Izazovi prijatelje': 'Challenge friends',
    'Često postavljena pitanja o quizu': 'Quiz FAQ',
    'Pitanje': 'Question',
    'Natrag': 'Back',
    'Otkaži': 'Cancel',
    'Nogomandala': 'Nogomandala',
    'Mod igre': 'Game mode',
    'Težina': 'Difficulty',
    'Svi igrači': 'All players',
    'Samo HNL': 'HNL only',
    'Lagano': 'Easy',
    'Normalno': 'Normal',
    'Teško': 'Hard',
    'Pogodi': 'Guess',
    'Preostalo pokušaja:': 'Guesses remaining:',
    'Otvori hint': 'Open hint',
    'Bravo!': 'Great job!',
    'Game over': 'Game over',
    'Podijeli rezultat': 'Share result',
    'Kako se igra?': 'How to play?',
    'Nogometni Wordle za HNL i svjetske igrače': 'Football Wordle for HNL and world players',
    'Generator timova - nasumična podjela u par klikova': 'Team generator - random team splitting in a few clicks',
    'nasumična podjela': 'random team splitting',
    'u par klikova': 'in a few clicks',
    'Unesi imena sudionika, odaberi broj timova i aplikacija će random podijeliti igrače u balansirane grupe. Idealno za rekreativni nogomet, školske sportove, team building radionice, kvizove, rođendane i sve ostale situacije gdje trebaš brzu i pravednu podjelu na timove - bez Excela, bez papirića, bez rasprave tko ide s kim.': 'Enter participant names, choose the number of teams and the app will randomly split players into balanced groups. Ideal for recreational football, school sports, team building workshops, quizzes, birthdays and any situation where you need a fast, fair team split without spreadsheets, paper slips or arguments.',
    'Nasumična podjela u timove nije samo za sport - koristan je svuda gdje više ljudi treba brzo i pravedno razvrstati u grupe.': 'Random team splitting is not only for sport - it is useful anywhere people need to be quickly and fairly split into groups.',
    'Rekreativni nogomet i mali sportovi': 'Recreational football and small-sided sports',
    'Mali nogomet, futsal, košarka na pola terena, odbojka na pijesku - brza random podjela prije utakmice, bez sukoba i beskonačnog biranja kapetana.': 'Small-sided football, futsal, half-court basketball, beach volleyball - quick random splitting before the match, without conflict or endless captain picks.',
    'Škola i vrtić - podjela razreda u grupe': 'School and kindergarten - split classes into groups',
    'Učitelji i odgajatelji: podijeli razred u radne grupe za projekt, natjecanje ili igru. Svaki put drugačija kombinacija, bez favoriziranja.': 'Teachers and educators: split a class into work groups for a project, competition or game. A different mix every time, without favoritism.',
    'Team building i firmaške radionice': 'Team building and company workshops',
    'Organiziraš team building? Podijeli kolege u timove za escape room, kviz ili radionicu - nitko ne može reći da je slagano namjerno.': 'Organizing team building? Split colleagues into teams for an escape room, quiz or workshop - nobody can say it was arranged on purpose.',
    'Kvizovi i pub quiz večeri': 'Quizzes and pub quiz nights',
    'Organizator kviza? Ubaci sve prijavljene, odaberi broj ekipa i rezultat je gotov - savršeno za nepoznate sudionike koji se miješaju u ekipe.': 'Running a quiz? Add all participants, choose the number of teams and the result is ready - perfect for mixing people who do not know each other.',
    'Rođendani i dječji tulumi': 'Birthdays and kids parties',
    'Potjere, štafete, potraga za blagom - svaka igra u timovima počinje podjelom. Generator to riješi dok roditelji postavljaju tortu.': 'Chases, relays, treasure hunts - every team game starts with a split. The generator handles it while the parents set up the cake.',
    'Društvene igre i turniri': 'Board games and tournaments',
    'Tabu, Activity, Pictionary, pikado turnir - svaka igra u kojoj treba balansirano podijeliti prijatelje u ekipe da bi bilo zanimljivo.': 'Taboo, Activity, Pictionary, darts tournaments - any game where friends need to be split into balanced teams to keep it fun.',
    'Bez registracije, bez aplikacije za preuzimanje, bez skrivenih troškova. Od unosa do rezultata - manje od 30 sekundi.': 'No registration, no app download, no hidden costs. From input to result in less than 30 seconds.',
    'Upiši ili zalijepi imena igrača - svako u novi red ili odvojeno zarezom.': 'Type or paste player names - one per line or separated by commas.',
    'Klikni 2, 3, 4, 5 ili 6 - koliko god timova trebaš za svoju aktivnost.': 'Click 2, 3, 4, 5 or 6 - however many teams your activity needs.',
    'Algoritam nasumično razvrsta igrače u timove podjednakih veličina.': 'The algorithm randomly sorts players into teams of similar size.',
    'Kopiraj podjelu jednim klikom i pošalji ekipi u WhatsApp ili Viber.': 'Copy the split with one click and send it to the squad on WhatsApp or Viber.',
    'Sve što trebaš znati prije nego što prvi put napraviš nasumičnu podjelu sudionika u timove preko ovog alata.': 'Everything you need to know before creating your first random team split with this tool.',
    'Je li generator timova stvarno besplatan?': 'Is the team generator really free?',
    'Da, generator timova na StatArena platformi potpuno je besplatan za svakoga. Ne tražimo registraciju, ne prikazujemo reklame i nema nikakvog ograničenja na broj podjela. Koristi koliko god puta trebaš za svoj trening, školski razred, team building, pub kviz ili rođendanski tulum.': 'Yes, the team generator on StatArena is completely free for everyone. We do not require registration, show ads or limit the number of splits. Use it as many times as you need for training, school, team building, pub quizzes or birthday parties.',
    'Koliko igrača mogu unijeti u generator?': 'How many players can I enter?',
    'Koliko timova mogu napraviti odjednom?': 'How many teams can I create at once?',
    'Je li podjela stvarno nasumična?': 'Is the split really random?',
    'Pamti li alat moje igrače i prošle podjele?': 'Does the tool remember my players and previous splits?',
    'Radi li generator na mobitelu?': 'Does the generator work on mobile?',
    'Radi li podjela balansirane timove po jačini?': 'Does it balance teams by skill?',
    'Mogu li podijeliti rezultat ekipi?': 'Can I share the result with the team?',
    'Organiziraš rekreativni nogomet s istom ekipom?': 'Do you organize recreational football with the same squad?',
    'Ako ista ekipa igra svaki tjedan, StatArena ti može napraviti puno više od nasumične podjele - balansirane timove po statistici igrača, raspored termina, MVP rang listu i arhivu cijele sezone. Sve besplatno.': 'If the same squad plays every week, StatArena can do much more than random splitting - balanced teams based on player stats, session scheduling, MVP leaderboard and a full season archive. All free.',
    'Registriraj svoju ekipu besplatno': 'Register your squad for free',
    'Saznaj više o StatAreni': 'Learn more about StatArena',
    'Raspored turnira - svi protiv svih ili kup u par klikova': 'Tournament schedule - round-robin or cup in a few clicks',
    'svi protiv svih ili kup': 'round-robin or cup',
    'Unesi ekipe, odaberi format turnira i aplikacija će automatski napraviti raspored utakmica - round-robin (svi protiv svih) ili kup sustav s eliminacijom. Idealno za mali nogomet, futsal, firmaške turnire, školska natjecanja i mini ligu među prijateljima. Bez Excela, bez ždrijeba iz šešira, bez rasprave tko igra s kim.': 'Enter teams, choose a tournament format and the app will automatically create the match schedule - round-robin or a knockout cup. Ideal for small-sided football, futsal, company tournaments, school competitions and mini leagues with friends. No spreadsheets, no drawing names from a hat, no arguments about who plays whom.',
    'Alat radi za bilo koji sport ili aktivnost u kojoj se više ekipa mora rasporediti po utakmicama - ne samo nogomet.': 'The tool works for any sport or activity where multiple teams need to be scheduled for matches - not just football.',
    'Mali nogomet i futsal turnir': 'Small-sided football and futsal tournament',
    'Mini liga među prijateljima': 'Mini league among friends',
    'Firmaški sportski dan': 'Company sports day',
    'Školska i studentska natjecanja': 'School and student competitions',
    'Košarka, odbojka, rukomet': 'Basketball, volleyball, handball',
    'Pikado, bilijar, stolni tenis': 'Darts, billiards, table tennis',
    'Bez registracije, bez papirića, bez Excel tablica. Od unosa ekipa do gotovog rasporeda - manje od minute.': 'No registration, no paper slips, no spreadsheets. From entering teams to a finished schedule in less than a minute.',
    'Upiši ili zalijepi nazive ekipa - svaki u novi red ili odvojen zarezom.': 'Type or paste team names - one per line or separated by commas.',
    'Svi protiv svih za mini ligu ili kup sustav za eliminaciju - klikni što ti treba.': 'Round-robin for a mini league or cup format for elimination - click what you need.',
    'Termin početka, trajanje utakmice, broj terena - alat sve automatski izračuna.': 'Start time, match duration, number of pitches - the tool calculates everything automatically.',
    'Kopiraj, pošalji u WhatsApp ili isprintaj - raspored je spreman za ekipu.': 'Copy it, send it to WhatsApp or print it - the schedule is ready for the teams.',
    'Sve što trebaš znati o generiranju rasporeda svi protiv svih, kup sustava i eliminacije za tvoj nogometni ili sportski turnir.': 'Everything you need to know about generating round-robin schedules, cup systems and elimination brackets for your football or sports tournament.',
    'Koja je razlika između svi protiv svih i kup sustava?': 'What is the difference between round-robin and cup format?',
    'Koliko ekipa mogu unijeti u generator rasporeda?': 'How many teams can I enter in the schedule generator?',
    'Što znači dvokružni sustav kod svi protiv svih?': 'What does double round-robin mean?',
    'Može li raspored pokriti više terena istovremeno?': 'Can the schedule cover multiple pitches at once?',
    'Alat izračunava vremena utakmica?': 'Does the tool calculate match times?',
    'Mogu li isprintati raspored turnira?': 'Can I print the tournament schedule?',
    'Radi li generator rasporeda za druge sportove?': 'Does the schedule generator work for other sports?',
    'Pamti li alat moj raspored i rezultate?': 'Does the tool remember my schedule and results?',
    'Je li algoritam rasporeda pravedan?': 'Is the schedule algorithm fair?',
    'Organiziraš rekreativni nogomet kroz cijelu sezonu?': 'Do you organize recreational football across a whole season?',
    'Probaj generator timova': 'Try the team generator',
    'Online semafor za nogomet, futsal i rekreativne termine': 'Online scoreboard for football, futsal and recreational sessions',
    'Ovaj digitalni semafor možeš otvoriti na mobitelu, tabletu, laptopu ili TV-u pored terena. Prati rezultat, vrijeme, poluvrijeme i kartone bez instalacije aplikacije i bez registracije.': 'You can open this digital scoreboard on a phone, tablet, laptop or TV next to the pitch. Track score, time, half and cards without installing an app or registering.',
    'Uključi full-screen prikaz i postavi uređaj pokraj terena. Rezultat i brojač vremena ostaju dovoljno veliki za ekipu, gledatelje i organizatora utakmice.': 'Turn on full-screen mode and place the device next to the pitch. The score and timer stay large enough for players, spectators and the organizer.',
    'Postavi trajanje poluvremena, pokreni timer i zaustavi ga kada treba pauza. Možeš koristiti odbrojavanje ili brojanje prema gore, ovisno o načinu igre.': 'Set the half duration, start the timer and pause it when needed. You can use countdown or count up, depending on how you play.',
    'Dodaj golove jednim klikom, smanji rezultat ako se pogriješi i prati žute i crvene kartone za obje ekipe. Sve je vidljivo na jednom čistom ekranu.': 'Add goals with one click, decrease the score if needed and track yellow and red cards for both teams. Everything is visible on one clean screen.',
    'Za koje utakmice možeš koristiti digitalni semafor': 'What matches you can use the digital scoreboard for',
    'Alat je napravljen za rekreativni nogomet, ali radi za svaki sport gdje trebaš jednostavan rezultat i timer.': 'The tool is built for recreational football, but works for any sport where you need a simple score and timer.',
    'Mali nogomet i futsal': 'Small-sided football and futsal',
    'Idealno za termine u dvorani, balonu ili na umjetnoj travi.': 'Ideal for indoor sessions, domes or artificial turf.',
    'Turniri i mini lige': 'Tournaments and mini leagues',
    'Koristi isti semafor kroz više utakmica i brzo resetiraj rezultat.': 'Use the same scoreboard across multiple matches and reset the score quickly.',
    'Školska natjecanja': 'School competitions',
    'Jednostavan prikaz za razredne turnire, sportske dane i treninge.': 'A simple display for class tournaments, sports days and training.',
    'Trening utakmice': 'Training matches',
    'Trener ili organizator može pratiti vrijeme, rezultat i kartone.': 'A coach or organizer can track time, score and cards.',
    'Kratki odgovori za sve koji traže semafor online, brojač vremena za nogomet ili digitalni rezultat za utakmicu.': 'Short answers for everyone looking for an online scoreboard, football timer or digital match score.',
    'Pucaj i brani jedanaesterce protiv kompjutera. Klik u gol šalje udarac, a klikom na gumb odabireš stranu na koju ćeš skočiti u obrani.': 'Take and save penalties against the computer. Click inside the goal to shoot, and click a button to choose which side to dive to when defending.',
    'Pritisni Start za početak meča': 'Press Start to begin the match',
    'Tvoj udarac - klikni u gol da pucaš': 'Your shot - click inside the goal to shoot',
    'AI šutira - odaberi stranu za obranu': 'AI shoots - choose a side to save',
    'Udarac...': 'Shot...',
    'Poraz - pokušaj ponovno!': 'Defeat - try again!',
    'Odigraj 5 udaraca i 5 obrana protiv kompjutera. Ako završi izjednačeno - ide sudden death.': 'Play 5 shots and 5 saves against the computer. If it ends tied, sudden death begins.',
    'Klikni u gol tamo gdje želiš pogoditi. Što bliže uglu, to veća šansa za gol.': 'Click in the goal where you want to shoot. The closer to the corner, the better the chance of scoring.',
    'Kad AI šutira, odaberi stranu (lijevo, sredina, desno) prije udarca.': 'When the AI shoots, choose a side (left, center, right) before the shot.',
    'Naizmjence tvoji i AI udarci. Vodi tko ima više golova na kraju.': 'You and the AI alternate shots. Whoever has more goals at the end wins.',
    'Ako je izjednačeno, igra se dok netko ne pogriješi.': 'If it is tied, play continues until someone misses.',
    'Brz, besplatan i jednostavan online simulator jedanaesteraca. Klik za udarac, gumb za obranu - bez registracije i bez instalacije.': 'A fast, free and simple online penalty simulator. Click to shoot, press a button to save - no registration or installation.',
    '10 pitanja. 60 sekundi. Otkrij kojeg od 16 svjetskih nogometaša najviše podsjećaš — od Modrića do Haalanda, od Neuera do Mbappéa.': '10 questions. 60 seconds. Discover which of 16 world footballers you resemble most - from Modric to Haaland, from Neuer to Mbappe.',
    'Brz, besplatan i jednostavan online quiz. 10 pitanja, jedan rezultat, slika spremna za dijeljenje. Bez registracije, bez instalacije.': 'A fast, free and simple online quiz. 10 questions, one result, image ready to share. No registration, no installation.',
    'Tvoj profil uspoređujemo sa 16 svjetskih nogometaša i biramo najbliže podudaranje.': 'We compare your profile with 16 world footballers and choose the closest match.',
    'PNG u formatu 1080×1920 — savršen za Instagram story i TikTok.': 'PNG in 1080×1920 format - perfect for Instagram stories and TikTok.',
    'Pošalji link, neka se i oni okušaju. Tko će dobiti istog igrača?': 'Send the link and let them try too. Who will get the same player?',
    'Pogodi tajnog nogometaša u': 'Guess the secret footballer in',
    'pokušaja': 'guesses',
    'Svaki dan novi igrač. Boje ti govore koliko si blizu: zelena = točno, žuta = blizu, siva = krivo. Strelice za skalarne vrijednosti pokazuju je li tajni igrač viši, stariji ili ima veći broj.': 'A new player every day. Colors tell you how close you are: green = correct, yellow = close, gray = wrong. Arrows for numeric values show whether the secret player is taller, older or has a higher number.',
    'igrača u modu': 'players in mode',
    'Upiši ime igrača (npr. Modrić, Livaja, Messi)...': 'Enter a player name (e.g. Modric, Livaja, Messi)...',
    'Ime igrača': 'Player name',
    'Nije pronađen igrač s tim imenom.': 'No player found with that name.',
    'Već si pokušao s tim igračem.': 'You already tried that player.',
    'Nije pronađen igrač. Pokušaj drugačije ime.': 'Player not found. Try another name.',
    'Hint nakon': 'Hint after',
    'Tajni igrač je bio:': 'The secret player was:',
    'Sutra novi puzzle!': 'New puzzle tomorrow!',
    'Igrač': 'Player',
    'Klub': 'Club',
    'Liga': 'League',
    'Pozicija': 'Position',
    'Nacionalnost': 'Nationality',
    'Dob': 'Age',
    'Broj': 'Number',
    'Visina': 'Height',
    'Upiši ime nogometaša koji misliš da je tajni igrač.': 'Enter the footballer you think is the secret player.',
    'Sustav usporedi': 'The system compares',
    'klub, ligu, poziciju, nacionalnost, dob, broj dresa i visinu': 'club, league, position, nationality, age, shirt number and height',
    'Zeleno': 'Green',
    'Žuto': 'Yellow',
    'Sivo': 'Gray',
    '= točno.': '= correct.',
    '= blizu': '= close',
    '= krivo.': '= wrong.',
    'Strelica gore (▲) znači da tajni igrač ima veću vrijednost.': 'The up arrow (▲) means the secret player has a higher value.',
    'Broj pokušaja ovisi o težini. Sutra u ponoć novi puzzle.': 'The number of guesses depends on difficulty. A new puzzle arrives at midnight tomorrow.',
    'Nogomandala je dnevna nogometna igra pogađanja u kojoj tražiš tajnog nogometaša pomoću tragova iz stvarnog nogometa. Svaki pokušaj uspoređuje klub, ligu, poziciju, nacionalnost, dob, broj dresa i visinu, pa se igra ponaša kao nogometni Wordle za navijače koji vole HNL, reprezentaciju i najveće svjetske klubove.': 'Nogomandala is a daily football guessing game where you find the secret footballer using real football clues. Each guess compares club, league, position, nationality, age, shirt number and height, so the game behaves like a football Wordle for fans of HNL, the national team and the biggest world clubs.',
    'Možeš igrati HNL mod s igračima iz hrvatske lige ili širi mod sa svim igračima u bazi. Novi puzzle dolazi svaki dan u lokalnu ponoć, a težinu možeš prilagoditi ovisno o tome želiš li lagani, normalni ili teži izazov.': 'You can play HNL mode with players from the Croatian league or a wider mode with all players in the database. A new puzzle arrives every day at local midnight, and you can adjust difficulty depending on whether you want an easy, normal or harder challenge.',
    'Koja je tvoja prirodna pozicija na terenu?': 'What is your natural position on the pitch?',
    'Napadač / "deveti"': 'Striker / number nine',
    'Krilo (lijevo / desno)': 'Winger (left / right)',
    'Vezni — kreativac': 'Midfielder - creator',
    'Defenzivni vezni': 'Defensive midfielder',
    'Bočni branič': 'Full-back',
    'Stoper': 'Center-back',
    'Vratar': 'Goalkeeper',
    'Što ti je najveći užitak na terenu?': 'What do you enjoy most on the pitch?',
    'Zabiti gol': 'Scoring a goal',
    'Dati ključnu asistenciju': 'Making the key assist',
    'Driblati i provući protivnika': 'Dribbling past an opponent',
    'Spektakularna obrana ili presretanje': 'A spectacular save or interception',
    'Tvoj fizički profil najbolje opisuje:': 'Your physical profile is best described as:',
    'Visok i jak — dominiram fizički': 'Tall and strong - physically dominant',
    'Brz kao munja': 'Fast as lightning',
    'Sitan, tehničan, brz na nogama': 'Small, technical and quick-footed',
    'Izdržljiv — trčim 90 minuta bez problema': 'Enduring - I can run 90 minutes easily',
    'Kako rješavaš pritisak protivnika?': 'How do you handle opponent pressure?',
    'Iskoristim brzinu da pobjegnem': 'I use speed to get away',
    'Odigram jednostavno, jedan-dva s suigračem': 'I play simple, one-two with a teammate',
    'Dribling, finta, izlazak': 'Dribble, feint, escape',
    'Šut iz prve, ne dam loptu': 'First-time shot, I do not give up the ball',
    'Tvoj omiljeni potez:': 'Your favorite move:',
    'Bicikl ili volej': 'Bicycle kick or volley',
    'Maradona finta / panna': 'Maradona turn / nutmeg',
    'Pas iza obrane "u prazno"': 'Pass behind the defense into space',
    'Klizeći start ili presretanje': 'Slide tackle or interception',
    'Tvoja uloga u svlačionici je:': 'Your role in the dressing room is:',
    'Vođa — motiviram ekipu': 'Leader - I motivate the team',
    'Tihi profesionalac': 'Quiet professional',
    'Šaljivac — razbijam tenziju': 'Joker - I break the tension',
    'Slušam i učim': 'I listen and learn',
    'Tvoj stil igre je više:': 'Your playing style is more:',
    'Eksplozivan i impulzivan': 'Explosive and impulsive',
    'Smiren i hladnokrvan': 'Calm and composed',
    'Tvrd i borben': 'Tough and combative',
    'Karizmatičan — nosim ekipu': 'Charismatic - I carry the team',
    'Ekipa gubi 0:1, 10 minuta do kraja. Što radiš?': 'Your team is losing 0-1 with 10 minutes left. What do you do?',
    'Sam idem riješiti — driblam i šutiram': 'I try to solve it myself - dribble and shoot',
    'Smirim igru, organiziram napad': 'I calm the game and organize the attack',
    'Trčim svuda, presing po lopti': 'I run everywhere, pressing the ball',
    'Idem prema gore i tražim pas iza obrane': 'I move forward and look for a pass behind the defense',
    'Tvoj idol — koji tip igrača?': 'Your idol - what type of player?',
    'Klasični napadač "9-ka"': 'Classic number 9 striker',
    'Kreativni "10-ka"': 'Creative number 10',
    'Box-to-box vezni': 'Box-to-box midfielder',
    'Stoper-vođa': 'Leader center-back',
    'Tvoja mentalna jakost najbolje opisuje:': 'Your mental strength is best described as:',
    'Stalno tražim loptu — želim biti u centru igre': 'I always ask for the ball - I want to be central',
    'Strpljiv — čekam svoj trenutak': 'Patient - I wait for my moment',
    'Borac do kraja — nikad ne odustajem': 'Fighter until the end - I never give up',
    'Hladna glava u najtežim trenucima': 'Cool head in the hardest moments',
    'Kako quiz odlučuje koji sam tip igrača?': 'How does the quiz decide what type of player I am?',
    'Mogu li ponoviti quiz?': 'Can I retake the quiz?',
    'Mogu li podijeliti rezultat na društvenim mrežama?': 'Can I share the result on social media?',
    'Tko su igrači u rezultatima?': 'Who are the players in the results?',
    'Je li quiz besplatan?': 'Is the quiz free?',
    'match': 'match',
    'Liga:': 'League:',
    'Pozicija:': 'Position:',
    'Kontinent reprezentacije:': 'National team continent:',
    'Visina je': 'Height is',
    'Broj dresa je': 'Shirt number is',
    '185 cm ili više': '185 cm or taller',
    'ispod 185 cm': 'under 185 cm',
    '20 ili veći': '20 or higher',
    'ispod 20': 'under 20',
    'Hint nakon 2. pokušaja': 'Hint after 2 guesses',
    'Hint nakon 3. pokušaja': 'Hint after 3 guesses',
    'Hint nakon 4. pokušaja': 'Hint after 4 guesses',
    'Pogodio si': 'You guessed',
    'u': 'in',
    'tajni igrač': 'secret player',
    'točno': 'correct',
    'blizu': 'close',
    'krivo': 'wrong',
    'veću': 'higher',
    'vrijednost': 'value',
  },
};

const normalizeText = (text) => text.replace(/\s+/g, ' ').trim();

const buildNormalizedDictionary = (dictionary) =>
  Object.entries(dictionary).reduce((next, [key, value]) => {
    next[normalizeText(key)] = value;
    return next;
  }, {});

const translateNodeText = (node, dictionary) => {
  const original = node.nodeValue;
  const normalized = normalizeText(original);
  if (!normalized) return;

  if (!dictionary[normalized]) {
    const translated = Object.entries(dictionary)
      .filter(([key]) => key.length > 3 && normalized.includes(key))
      .reduce((value, [key, translatedValue]) => value.replace(key, translatedValue), normalized);

    if (translated !== normalized) {
      node.nodeValue = translated;
    }
    return;
  }

  node.nodeValue = dictionary[normalized];
};

const translateStaticDom = (root, dictionary) => {
  if (!root || !dictionary) return;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (!parent || ['SCRIPT', 'STYLE', 'TEXTAREA', 'INPUT'].includes(parent.tagName)) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  const textNodes = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode);
  textNodes.forEach((node) => translateNodeText(node, dictionary));

  root.querySelectorAll('[placeholder], [aria-label], [title]').forEach((el) => {
    ['placeholder', 'aria-label', 'title'].forEach((attr) => {
      const value = el.getAttribute(attr);
      const normalized = value ? normalizeText(value) : '';
      if (normalized && dictionary[normalized]) {
        el.setAttribute(attr, dictionary[normalized]);
      }
    });
  });
};

const LanguageContext = createContext(null);

const getInitialLanguage = () => {
  if (typeof window === 'undefined') return 'hr';

  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (SUPPORTED_LANGUAGES.includes(stored)) return stored;

  const browserLanguage = window.navigator.language?.slice(0, 2);
  return SUPPORTED_LANGUAGES.includes(browserLanguage) ? browserLanguage : 'hr';
};

const getByPath = (source, path) =>
  path.split('.').reduce((value, key) => (value == null ? value : value[key]), source);

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(getInitialLanguage);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    if (typeof document === 'undefined' || language !== 'en') return undefined;

    const dictionary = buildNormalizedDictionary(staticTextTranslations.en);
    const translate = () => translateStaticDom(document.body, dictionary);
    const frame = window.requestAnimationFrame(translate);
    const observer = new MutationObserver(() => {
      window.requestAnimationFrame(translate);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['placeholder', 'aria-label', 'title'],
    });

    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [language]);

  const value = useMemo(() => {
    const setLanguage = (nextLanguage) => {
      if (SUPPORTED_LANGUAGES.includes(nextLanguage)) {
        setLanguageState(nextLanguage);
      }
    };

    const t = (path) => {
      const translated = getByPath(translations[language], path);
      const fallback = getByPath(translations.hr, path);
      return translated ?? fallback ?? path;
    };

    return {
      language,
      setLanguage,
      toggleLanguage: () => setLanguage(language === 'hr' ? 'en' : 'hr'),
      t,
    };
  }, [language]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within LanguageProvider');
  }
  return context;
};
